import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'

export type CheckboxMultiSelectOption = {
  value: string
  label: string
}

type CheckboxMultiSelectProps = {
  options: CheckboxMultiSelectOption[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  /** Quando true, exibe campo de busca no topo do dropdown. */
  searchable?: boolean
  searchPlaceholder?: string
}

export const CheckboxMultiSelect: React.FC<CheckboxMultiSelectProps> = ({
  options,
  selected,
  onChange,
  placeholder = 'Todos',
  disabled = false,
  className = '',
  searchable = false,
  searchPlaceholder = 'Buscar…',
}) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) {
      setSearch('')
      return
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  useEffect(() => {
    if (open && searchable) {
      searchInputRef.current?.focus()
    }
  }, [open, searchable])

  const selectedSet = new Set(selected)
  const selectedLabels = options.filter(o => selectedSet.has(o.value)).map(o => o.label)

  const triggerLabel =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? selectedLabels[0]
        : `${selected.length} selecionados`

  const filteredOptions = useMemo(() => {
    if (!searchable || !search.trim()) return options
    const q = search.trim().toLowerCase()
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q),
    )
  }, [options, search, searchable])

  const toggle = (value: string, checked: boolean) => {
    if (checked) {
      onChange([...selected, value])
    } else {
      onChange(selected.filter(v => v !== value))
    }
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(prev => !prev)}
        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-left text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between gap-2"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">{triggerLabel}</span>
        <ChevronDownIcon className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute z-50 left-0 mt-1 min-w-full w-max max-w-[min(22rem,calc(100vw-2rem))] max-h-56 overflow-y-auto overflow-x-hidden rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg py-1"
          role="listbox"
        >
          {searchable && (
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 px-2 pb-1 pt-1 border-b border-gray-100 dark:border-gray-700">
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder={searchPlaceholder}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          )}
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">Nenhuma opção</div>
          ) : (
            filteredOptions.map(option => {
              const checked = selectedSet.has(option.value)
              return (
                <label
                  key={option.value}
                  className="flex items-start gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-900 dark:text-white"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={e => toggle(option.value, e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 dark:bg-gray-700"
                  />
                  <span className="min-w-0 flex-1 whitespace-normal break-words leading-snug">{option.label}</span>
                </label>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
